import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Menu, X, ArrowRight, Zap, Shield, BarChart3, Cloud, Headphones } from 'lucide-react';
import './Website.css';

export const Website: React.FC = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [demoDialogOpen, setDemoDialogOpen] = useState(false);
  const [demoFormData, setDemoFormData] = useState({
    name: '',
    company: '',
    email: '',
    message: '',
  });
  const [demoSubmitted, setDemoSubmitted] = useState(false);

  const handleDemoSubmit = () => {
    if (demoFormData.name && demoFormData.company && demoFormData.email) {
      console.log('Demo request submitted:', demoFormData);
      setDemoSubmitted(true);
      setTimeout(() => {
        setDemoDialogOpen(false);
        setDemoFormData({ name: '', company: '', email: '', message: '' });
        setDemoSubmitted(false);
      }, 2000);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation from-blue-900 to-blue-800 bg-gradient-to-r */}
      <nav className="sticky top-0 z-50 bg-white  shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex-shrink-0">
              {/* <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                <img src='/logo1.png' className='h-45'></img> Oracle E-Invoicing
              </h1> */}
              <div className="flex items-center gap-2">
                <img src="/logo.webp" alt="Logo" className="w-8 h-8" />
                <span className="text-2xl font-bold bg-gradient-to-r from-blue-800 to-cyan-800 bg-clip-text text-transparent">
                  Oracle E-Invoicing
                </span>
              </div>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <button
                onClick={() => scrollToSection('features')}
                className="text-blue hover:text-blue-200 transition"
              >
                Features
              </button>
              <button
                onClick={() => scrollToSection('benefits')}
                className="text-blue hover:text-blue-200 transition"
              >
                Benefits
              </button>
              <button
                onClick={() => scrollToSection('pricing')}
                className="text-blue hover:text-blue-200 transition"
              >
                Pricing
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="text-blue hover:text-blue-200 transition"
              >
                Contact
              </button>
              <button
                onClick={() => navigate('/login')}
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg transition"
              >
                Login
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden bg-blue-800 px-4 py-4 space-y-2">
              <button
                onClick={() => scrollToSection('features')}
                className="block w-full text-left text-white hover:text-blue-200 py-2"
              >
                Features
              </button>
              <button
                onClick={() => scrollToSection('benefits')}
                className="block w-full text-left text-white hover:text-blue-200 py-2"
              >
                Benefits
              </button>
              <button
                onClick={() => scrollToSection('pricing')}
                className="block w-full text-left text-white hover:text-blue-200 py-2"
              >
                Pricing
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="block w-full text-left text-white hover:text-blue-200 py-2"
              >
                Contact
              </button>
              <button
                onClick={() => navigate('/login')}
                className="block w-full bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition"
              >
                Login
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Unified E-Invoicing for Oracle ERP
          </h2>
          <p className="text-xl md:text-2xl mb-8 opacity-90">
            Seamless FTA & PEPPOL Compliance Across All Oracle Systems
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setDemoDialogOpen(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold transition transform hover:scale-105"
            >
              Request Demo
            </button>
            <button
              onClick={() => navigate('/login')}
              className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-3 rounded-lg font-semibold transition"
            >
              Login to Platform
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-3xl md:text-4xl font-bold text-center text-blue-900 mb-12">
            Powerful Features
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Zap,
                title: 'Lightning Fast',
                description: 'Process 10,000 invoices per hour with real-time validation',
              },
              {
                icon: Shield,
                title: 'Enterprise Security',
                description: 'End-to-end encryption, digital signatures, and audit trails',
              },
              {
                icon: BarChart3,
                title: 'Advanced Analytics',
                description: 'Real-time dashboards with compliance monitoring',
              },
              {
                icon: Cloud,
                title: 'Multi-ERP Support',
                description: 'Seamless integration with EBS, Fusion, NetSuite, JDE, PeopleSoft',
              },
              {
                icon: CheckCircle,
                title: '100% Compliant',
                description: 'Full FTA and PEPPOL compliance with automatic validation',
              },
              {
                icon: Headphones,
                title: '24/7 Support',
                description: 'Dedicated support team available round the clock',
              },
            ].map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition transform hover:-translate-y-2"
                >
                  <Icon className="w-12 h-12 text-blue-500 mb-4" />
                  <h4 className="text-xl font-bold text-blue-900 mb-2">{feature.title}</h4>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Key Capabilities Section */}
      <section id="benefits" className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-3xl md:text-4xl font-bold text-center text-blue-900 mb-12">
            Key Capabilities
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow">
              <h4 className="text-2xl font-bold text-blue-900 mb-6">AR Invoice Processing</h4>
              <ul className="space-y-3">
                {[
                  'Automatic extraction from all ERP systems',
                  'Real-time FTA compliance validation',
                  'Digital signature generation',
                  'Direct FTA Portal submission',
                  'Complete audit trail',
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-8 rounded-lg shadow">
              <h4 className="text-2xl font-bold text-blue-900 mb-6">AP Invoice Processing</h4>
              <ul className="space-y-3">
                {[
                  'Automatic AIS Portal integration',
                  'Signature verification',
                  'Duplicate detection',
                  'Three-way matching with PO',
                  'Automated GL account mapping',
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Supported Systems - Detailed Cards */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-3xl md:text-4xl font-bold text-center text-blue-900 mb-12">
            Supported ERP Systems
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              {
                name: 'Oracle EBS',
                icon: '🏢',
                description: 'E-Business Suite',
                features: [
                  'Direct database connection',
                  'Multi-org support',
                  'GL account mapping',
                  'Concurrent requests',
                  '1,000 invoices/min',
                ],
                color: 'from-red-500 to-red-600',
              },
              {
                name: 'Oracle Fusion',
                icon: '☁️',
                description: 'Cloud ERP',
                features: [
                  'REST API integration',
                  'OAuth 2.0 auth',
                  'Multi-legal entity',
                  'Real-time retrieval',
                  '<500ms response',
                ],
                color: 'from-orange-500 to-orange-600',
              },
              {
                name: 'NetSuite',
                icon: '🌐',
                description: 'Cloud Accounting',
                features: [
                  'SuiteScript 2.0',
                  'RESTlet integration',
                  'Multi-subsidiary',
                  'Custom fields',
                  '10 concurrent req',
                ],
                color: 'from-blue-500 to-blue-600',
              },
              {
                name: 'JD Edwards',
                icon: '⚙️',
                description: 'EnterpriseOne',
                features: [
                  'Orchestrator APIs',
                  'Real-time processing',
                  'Multi-company',
                  'Batch processing',
                  'Error recovery',
                ],
                color: 'from-green-500 to-green-600',
              },
              {
                name: 'PeopleSoft',
                icon: '🔧',
                description: 'Enterprise HCM',
                features: [
                  'Integration Broker',
                  'Message processing',
                  'Multi-business unit',
                  'Async processing',
                  '500 invoices/min',
                ],
                color: 'from-purple-500 to-purple-600',
              },
            ].map((system, index) => (
              <div
                key={index}
                className={`bg-gradient-to-br ${system.color} text-white rounded-lg p-6 shadow-lg hover:shadow-2xl transition transform hover:-translate-y-2`}
              >
                <div className="text-4xl mb-3">{system.icon}</div>
                <h4 className="text-xl font-bold mb-1">{system.name}</h4>
                <p className="text-sm opacity-90 mb-4">{system.description}</p>
                <div className="border-t border-white border-opacity-30 pt-4">
                  <ul className="space-y-2">
                    {system.features.map((feature, fIndex) => (
                      <li key={fIndex} className="text-sm flex items-start gap-2">
                        <span className="text-lg leading-none">✓</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-3xl md:text-4xl font-bold text-center text-blue-900 mb-12">
            Pricing Plans
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: 'Starter',
                price: 'AED 50,000',
                period: '/year',
                features: [
                  'Up to 10,000 invoices/month',
                  '1 ERP System',
                  'Basic Dashboard',
                  'Email Support',
                ],
              },
              {
                name: 'Professional',
                price: 'AED 150,000',
                period: '/year',
                features: [
                  'Up to 100,000 invoices/month',
                  'Up to 3 ERP Systems',
                  'Advanced Analytics',
                  'Priority Support',
                ],
                highlighted: true,
              },
              {
                name: 'Enterprise',
                price: 'Custom',
                period: 'pricing',
                features: [
                  'Unlimited invoices',
                  'All 5 ERP Systems',
                  'Custom Integration',
                  '24/7 Dedicated Support',
                ],
              },
            ].map((plan, index) => (
              <div
                key={index}
                className={`rounded-lg p-8 transition transform hover:shadow-xl ${plan.highlighted
                    ? 'bg-white border-4 border-blue-500 scale-105'
                    : 'bg-white border border-gray-200'
                  }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-bold">
                      POPULAR
                    </span>
                  </div>
                )}
                <h4 className="text-2xl font-bold text-blue-900 mb-2">{plan.name}</h4>
                <div className="text-4xl font-bold text-blue-500 mb-1">{plan.price}</div>
                <p className="text-gray-600 mb-6">{plan.period}</p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => setDemoDialogOpen(true)}
                  className={`w-full py-3 rounded-lg font-semibold transition ${plan.highlighted
                      ? 'bg-blue-500 text-white hover:bg-blue-600'
                      : 'border-2 border-blue-500 text-blue-500 hover:bg-blue-50'
                    }`}
                >
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Login Card Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-3xl md:text-4xl font-bold text-center text-blue-900 mb-12">
            Try the Platform
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gray-50 p-8 rounded-lg">
              <h4 className="text-2xl font-bold text-blue-900 mb-6">Demo Credentials</h4>
              <div className="bg-white p-6 rounded-lg mb-4 border border-gray-200">
                <p className="text-sm text-gray-600 mb-2">Username:</p>
                <p className="font-mono bg-gray-100 p-3 rounded mb-4 break-all">admin@example.com</p>
                <p className="text-sm text-gray-600 mb-2">Password:</p>
                <p className="font-mono bg-gray-100 p-3 rounded break-all">password123</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-6">
                <p className="text-blue-900 text-sm">
                  Use these credentials to explore the full platform features
                </p>
              </div>
              <button
                onClick={() => navigate('/login')}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold transition flex items-center justify-center gap-2"
              >
                Access Platform <ArrowRight size={20} />
              </button>
            </div>
            <div className="bg-gray-50 p-8 rounded-lg">
              <h4 className="text-2xl font-bold text-blue-900 mb-6">Platform Highlights</h4>
              <ul className="space-y-3">
                {[
                  'Real-time invoice processing dashboard',
                  'Multi-ERP system integration',
                  'FTA & PEPPOL compliance validation',
                  'Advanced analytics and reporting',
                  'Comprehensive audit trails',
                  'Digital signature management',
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-3xl md:text-4xl font-bold text-center mb-12">Get in Touch</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8 text-center">
            <div>
              <p className="text-2xl mb-2">📧</p>
              <h4 className="text-xl font-bold mb-2">Email</h4>
              <p>info@oracleadapter.ae</p>
            </div>
            <div>
              <p className="text-2xl mb-2">📞</p>
              <h4 className="text-xl font-bold mb-2">Phone</h4>
              <p>+971-4-XXX-XXXX</p>
            </div>
            <div>
              <p className="text-2xl mb-2">🌐</p>
              <h4 className="text-xl font-bold mb-2">Website</h4>
              <p>www.oracleadapter.ae</p>
            </div>
          </div>
          <div className="text-center">
            <button
              onClick={() => setDemoDialogOpen(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold transition"
            >
              Request Demo
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-blue-950 text-white py-8 px-4 text-center">
        <div className="max-w-7xl mx-auto">
          <p className="text-sm">© 2026 Oracle ERP Universal UAE E-Invoicing Adapter. All rights reserved.</p>
          <p className="text-sm mt-2">Designed for FTA and PEPPOL Compliance</p>
        </div>
      </footer>

      {/* Demo Request Dialog */}
      {demoDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full shadow-xl">
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white p-6 rounded-t-lg">
              <h3 className="text-2xl font-bold">Request a Demo</h3>
            </div>
            <div className="p-6">
              {demoSubmitted ? (
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                  <p className="text-green-900">
                    ✓ Thank you! We'll contact you soon to schedule your demo.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={demoFormData.name}
                    onChange={(e) => setDemoFormData({ ...demoFormData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                  <input
                    type="text"
                    placeholder="Company Name"
                    value={demoFormData.company}
                    onChange={(e) => setDemoFormData({ ...demoFormData, company: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={demoFormData.email}
                    onChange={(e) => setDemoFormData({ ...demoFormData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                  <textarea
                    placeholder="Message (Optional)"
                    rows={4}
                    value={demoFormData.message}
                    onChange={(e) => setDemoFormData({ ...demoFormData, message: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  />
                </div>
              )}
            </div>
            <div className="flex gap-4 p-6 border-t border-gray-200">
              <button
                onClick={() => setDemoDialogOpen(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              {!demoSubmitted && (
                <button
                  onClick={handleDemoSubmit}
                  disabled={!demoFormData.name || !demoFormData.company || !demoFormData.email}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Submit
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
